/**
 * Knowledge base list / detail / create / delete hooks.
 *
 * KB list state caches `{ items, nextToken }` (the codebase's
 * canonical paginated shape) under `['kb', 'list', scope]`.
 */

import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { kbApi, meApi } from '../services/api';
import type {
  CreateKnowledgeBaseInput,
  KbScope,
  KnowledgeBase,
} from '../types/kb';

const KB_LIST_KEY = (scope?: KbScope) => ['kb', 'list', scope ?? 'all'] as const;
const KB_ME_LIST_KEY = ['me', 'kb', 'list'] as const;
const KB_DETAIL_KEY = (kbId: string) => ['kb', kbId] as const;

/**
 * Space-scoped KB list (`GET /api/v1/kb`). Resolves the active space
 * from the authorizer; `scope` narrows to just space- or org-owned rows.
 * Use this from space-scoped surfaces (console, agent editor).
 */
export function useKnowledgeBases(opts: { scope?: KbScope } = {}) {
  return useQuery({
    queryKey: KB_LIST_KEY(opts.scope),
    queryFn: () => kbApi.list({ scope: opts.scope, limit: 100 }),
    staleTime: 30_000,
  });
}

/**
 * Cross-space KB aggregator (`GET /api/v1/me/kb`). Returns every KB the
 * user can see across every space they belong to. Use this on the
 * operator surface where there's no active space.
 */
export function useMeKnowledgeBases() {
  return useQuery({
    queryKey: KB_ME_LIST_KEY,
    queryFn: () => meApi.kb({ limit: 100 }),
    staleTime: 30_000,
  });
}

/**
 * Convenience over `useMeKnowledgeBases()` for surfaces that just want a
 * flat list of every KB the user can touch.
 */
export function useAllKnowledgeBases() {
  const queryClient = useQueryClient();
  const query = useMeKnowledgeBases();
  const items: KnowledgeBase[] = useMemo(
    () => query.data?.items ?? [],
    [query.data],
  );
  return {
    items,
    isLoading: query.isLoading,
    error: query.error ?? null,
    refetch: query.refetch,
    queryClient,
  };
}

export function useKnowledgeBase(kbId: string | undefined) {
  return useQuery({
    queryKey: KB_DETAIL_KEY(kbId ?? ''),
    queryFn: () => kbApi.get(kbId as string),
    enabled: !!kbId,
    staleTime: 30_000,
  });
}

export function useCreateKnowledgeBase() {
  const queryClient = useQueryClient();
  return useMutation<KnowledgeBase, Error, CreateKnowledgeBaseInput>({
    mutationFn: (input) => kbApi.create(input),
    onSuccess: (kb) => {
      queryClient.setQueryData(KB_DETAIL_KEY(kb.kbId), kb);
      queryClient.invalidateQueries({ queryKey: ['kb', 'list'] });
      queryClient.invalidateQueries({ queryKey: KB_ME_LIST_KEY });
    },
  });
}

export function useDeleteKnowledgeBase() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { kbId: string }>({
    mutationFn: ({ kbId }) => kbApi.remove(kbId),
    onSuccess: (_void, { kbId }) => {
      queryClient.removeQueries({ queryKey: KB_DETAIL_KEY(kbId) });
      queryClient.removeQueries({ queryKey: ['kb', kbId] });
      queryClient.invalidateQueries({ queryKey: ['kb', 'list'] });
      queryClient.invalidateQueries({ queryKey: KB_ME_LIST_KEY });
    },
  });
}
